import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Skeleton from '@/common/components/skeleton/Skeleton';
import useImageLoader from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import ChatVideoPlayer from '../chatMedia/ChatVideoPlayer';
import { CHAT_MEDIA_GALLERY_TYPE } from '../../utils/getConversationMediaFromMessages';
import {
	IconButton,
	ImageSkeleton,
	ImageStage,
	ModalBackdrop,
	ModalCounter,
	ModalHeader,
	ModalImage,
	ModalShell,
	NextButton,
	PrevButton,
} from './ImageGalleryModal.styles';

const ChevronLeftIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="m14 7-5 5 5 5" />
	</svg>
);

const ChevronRightIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="m10 7 5 5-5 5" />
	</svg>
);

const CloseIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="m7 7 10 10M17 7 7 17" />
	</svg>
);

const ImageGalleryModal = ({
	images,
	currentIndex,
	onClose,
	onPrev,
	onNext,
}) => {
	const t = useLanguageData('chat');
	const currentItem = images[currentIndex];
	const isVideo = currentItem?.type === CHAT_MEDIA_GALLERY_TYPE.VIDEO;
	const { imageSrc, isLoading: isImageLoading } = useImageLoader(
		isVideo ? null : currentItem?.url,
	);
	const {
		videoSrc,
		isLoading: isVideoLoading,
		hasError: hasVideoError,
	} = useVideoLoader(isVideo ? currentItem?.url : null);
	const [isImageReady, setIsImageReady] = useState(false);

	const canGoPrev = currentIndex > 0;
	const canGoNext = currentIndex < images.length - 1;
	const showCounter = images.length > 1;

	useEffect(() => {
		setIsImageReady(false);
	}, [currentIndex, imageSrc, isVideo]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				onClose();
				return;
			}

			if (event.key === 'ArrowLeft' && canGoPrev) {
				onPrev();
				return;
			}

			if (event.key === 'ArrowRight' && canGoNext) {
				onNext();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [canGoNext, canGoPrev, onClose, onNext, onPrev]);

	const handleImageLoad = useCallback(() => {
		setIsImageReady(true);
	}, []);

	const showSkeleton = isVideo
		? isVideoLoading || !videoSrc
		: isImageLoading || !imageSrc || !isImageReady;
	const showImage = !isVideo && Boolean(imageSrc) && isImageReady;

	return createPortal(
		<ModalBackdrop onClick={onClose} role="dialog" aria-modal="true">
			<ModalShell onClick={(event) => event.stopPropagation()}>
				<ModalHeader>
					<IconButton
						type="button"
						onClick={onPrev}
						disabled={!canGoPrev}
						aria-label={t.galleryPrev}
					>
						<ChevronLeftIcon />
					</IconButton>
					{showCounter ? (
						<ModalCounter>
							{currentIndex + 1} / {images.length}
						</ModalCounter>
					) : (
						<ModalCounter aria-hidden="true">&nbsp;</ModalCounter>
					)}
					<IconButton type="button" onClick={onClose} aria-label={t.galleryClose}>
						<CloseIcon />
					</IconButton>
				</ModalHeader>
				<ImageStage>
					{showSkeleton && !isVideo && (
						<ImageSkeleton>
							<Skeleton
								width="100%"
								height="100%"
								minWidth="100%"
								minHeight="100%"
								radius="0"
							/>
						</ImageSkeleton>
					)}
					{isVideo ? (
						<ChatVideoPlayer
							key={`${currentIndex}-${videoSrc}`}
							src={videoSrc}
							isLoading={isVideoLoading || !videoSrc}
							hasError={hasVideoError}
							errorText={t.videoLoadError}
						/>
					) : null}
					{!isVideo && imageSrc ? (
						<ModalImage
							key={`${currentIndex}-${imageSrc}`}
							ref={(element) => {
								if (element?.complete) {
									setIsImageReady(true);
								}
							}}
							src={imageSrc}
							alt={
								isVideo
									? t.generatedVideoAlt
									: currentItem?.caption || t.generatedImageAlt
							}
							$visible={showImage}
							onLoad={handleImageLoad}
						/>
					) : null}
					<PrevButton
						type="button"
						onClick={onPrev}
						disabled={!canGoPrev}
						aria-label={t.galleryPrev}
					>
						<ChevronLeftIcon />
					</PrevButton>
					<NextButton
						type="button"
						onClick={onNext}
						disabled={!canGoNext}
						aria-label={t.galleryNext}
					>
						<ChevronRightIcon />
					</NextButton>
				</ImageStage>
			</ModalShell>
		</ModalBackdrop>,
		document.body,
	);
};

export default ImageGalleryModal;
